import { useCallback, useEffect, useState } from "react";

import { FormType, Locales } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

interface UseFundSelectionFormOptions {
  formId?: string;
  formType?: FormType;
  locale: Locales;
  enabled?: boolean;
  onSaved?: () => void;
}

export const useFundSelectionForm = ({
  formId,
  formType,
  locale,
  enabled = true,
  onSaved,
}: UseFundSelectionFormOptions) => {
  const { t } = useTranslate();
  const utils = trpc.useUtils();
  const { mutateAsync: updateFormFunds, isPending: isSaving } = trpc.updateFormFunds.useMutation();

  const { data: formFundsResult } = trpc.getFormFunds.useQuery(
    { formId: formId!, formType: formType!, locale },
    { enabled: Boolean(formId && formType && enabled) }
  );

  const formHasMoreThanOneFund = (formFundsResult?.funds.length ?? 0) > 1;

  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);

  // Sync local state with server data when form funds are loaded
  useEffect(() => {
    if (formFundsResult) {
      setSelectedFundIds(formFundsResult.funds.map((f) => f.fundId));
    }
  }, [formFundsResult]);

  const handleSave = useCallback(
    async (isRequired: boolean) => {
      if (!formId || !formType) {
        return;
      }
      const { error } = await updateFormFunds({ formId, formType, fundIds: selectedFundIds, isRequired, locale });
      if (error) {
        enqueueSnackbar(t("dashboard", "funds.editDesignation.updateError"), { vibe: "danger" });
        return;
      }
      await utils.getFormFunds.invalidate({ formId, formType, locale });
      onSaved?.();
    },
    [formId, formType, locale, selectedFundIds, updateFormFunds, utils, onSaved, t]
  );

  const hasEnoughFundsSelected = selectedFundIds.length >= 2;

  const resetState = () => setSelectedFundIds([]);

  return {
    selectedFundIds,
    setSelectedFundIds,
    hasEnoughFundsSelected,
    formHasMoreThanOneFund,
    formFundsResult,
    isSaving,
    handleSave,
    resetState,
  };
};
